// Sudoku history schema. `GameRecord` is the finished-puzzle row; `ActiveSession`
// is the in-progress snapshot stashed in localStorage so a refresh resumes the
// solve. Both are versioned by `HISTORY_SCHEMA_VERSION` so a future shape
// change cleanly invalidates older blobs.
//
// `SolveEvent` carries `ReadonlySet<NonEmptyDigit>` fields in-memory. For the
// on-disk shape we widen them to `NonEmptyDigit[]` since JSON cannot represent
// sets — see `toSerializableEvent` / `fromSerializableEvent` in
// `ActiveGameStore.ts`.

import type { SchemaVersioned } from '@bn-games/history-core';
import type { Digit, NonEmptyDigit, Puzzle, Difficulty } from '../game/types.js';

export { generateId } from '@bn-games/history-core';

// v1: initial sudoku schema.
export const HISTORY_SCHEMA_VERSION = 1;

export const HISTORY_CAP = 50;
export const HISTORY_STORAGE_KEY = 'sudoku-history';
export const ACTIVE_GAME_STORAGE_KEY = 'sudoku-active-game';

export type SerializableSolveEvent =
  | {
      readonly kind: 'enter';
      readonly cellIndex: number;
      readonly prevDigit: Digit;
      readonly newDigit: NonEmptyDigit;
      readonly clearedPencils: readonly NonEmptyDigit[];
      /** Optional: present on events recorded after eager-elimination shipped.
       *  Absent on resumed sessions from an older blob — treated as empty. */
      readonly clearedPeerPencils?: readonly number[];
      readonly wasError: boolean;
    }
  | {
      readonly kind: 'erase';
      readonly cellIndex: number;
      readonly prevDigit: Digit;
      readonly prevPencils: readonly NonEmptyDigit[];
    }
  | {
      readonly kind: 'pencil';
      readonly cellIndex: number;
      readonly digit: NonEmptyDigit;
      readonly added: boolean;
    };

export interface ActiveSession extends SchemaVersioned {
  readonly id: string;
  readonly puzzle: Puzzle;
  readonly user: readonly Digit[];
  /** Per-cell candidate sets, serialized as arrays. */
  readonly pencil: readonly (readonly NonEmptyDigit[])[];
  readonly history: readonly SerializableSolveEvent[];
  readonly redoStack: readonly SerializableSolveEvent[];
  readonly startedAt: number;
  readonly hintsUsed: number;
  readonly errorsMade: number;
  readonly updatedAt: number;
}

export interface GameRecord extends SchemaVersioned {
  readonly id: string;
  readonly seed: number;
  readonly difficulty: Difficulty;
  readonly generatorVersion: number;
  readonly timeMs: number;
  readonly hintsUsed: number;
  readonly errorsMade: number;
  /** Ms epoch the puzzle was completed. `HistoryStore` requires `finishedAt`. */
  readonly finishedAt: number;
  /** True when the player gave up and revealed the solution from the pause
   *  menu. Absent on older records — treat as `false`. */
  readonly forfeited?: boolean;
}
